import React, { useEffect, useState, useContext, useMemo, useCallback } from "react";
import { adminApi } from "../apiAdmin";
import { AdminAuthContext } from "../context/AdminAuthContext";
import "./AdminDashboardStyles.css";
import { useNavigate } from "react-router-dom";
import AdminOrdersTab from "../components/AdminOrdersTab";
import AdminOverviewTab from "../components/AdminOverviewTab";
import AdminEstablishmentsTab from "../components/AdminEstablishmentsTab";
import AdminAllPayoutsTab from "../components/AdminAllPayoutsTab";
import AdminTrendingTab from "../components/AdminTrendingTab";
import AdminDisputesTab from "../components/AdminDisputesTab";

type Tab = "overview" | "users" | "orders" | "admins" | "revenue" | "establishments" | "allPayouts" | "trending" | "disputes";

interface User {
  _id: string;
  name: string;
  email: string;
  role?: string;
  slug?: string;
  avatarUrl?: string;
  isFrozen?: boolean;
  freezeType?: "temporary" | "permanent" | null;
  freezeReason?: string | null;
  frozenAt?: string | null;
  frozenUntil?: string | null;
  isStripeOnboarded?: boolean;
  isSmsEnabled?: boolean;
  phoneNumber?: string | null;
}

interface Order {
  _id: string;
  dj: string | { _id: string; name: string; avatarUrl?: string };
  searchQuery: string;
  amount: number;
  createdAt: string;
  status?: "pending" | "queued" | "accepted" | "declined" | "played";
  currency?: string;
  totalAmount?: number;
  feeAmount?: number;
  paid?: boolean;
  trackImage?: string;
  trackArtists?: string[];
  trackAlbum?: string;
  customerEmail?: string;
}

interface PayoutEvent {
  _id: string;
  batchId: string;
  createdAt: string;
  totalGross: number;
  totalNet: number;
  ordersCount: number;
  ordersAccepted: number;
  ordersDeclined: number;
  avgRequestValue: number;
}

function fmtAvatar(url?: string): string | null {
  if (!url) return null;
  return url.startsWith("http") ? url : `https://api.requesta.app${url}`;
}

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: "overview", label: "Dashboard", icon: "⊞" },
  { id: "orders", label: "Requests", icon: "R" },
  { id: "users", label: "Talent", icon: "🎧" },
  { id: "revenue", label: "Revenue", icon: "📈" },
  { id: "allPayouts", label: "All Payouts", icon: "💸" },
  { id: "trending", label: "Trending", icon: "🔥" },
  { id: "disputes", label: "Disputes", icon: "⚠️" },
  { id: "establishments", label: "Venues", icon: "🏢" },
  { id: "admins", label: "Admins", icon: "🔐" },
];

export default function AdminDashboard() {
  const { admin, logout } = useContext(AdminAuthContext);
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("overview");

  const [users, setUsers] = useState<User[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [admins, setAdmins] = useState<User[]>([]);

  // Modal state
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [confirmIsAdmin, setConfirmIsAdmin] = useState(false);
  const [passwordId, setPasswordId] = useState<string | null>(null);
  const [passwordIsAdmin, setPasswordIsAdmin] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [createAdmin, setCreateAdmin] = useState({ name: "", email: "", password: "" });
  const [createError, setCreateError] = useState("");
  const [userQuery, setUserQuery] = useState("");

  // Payouts panel
  const [payoutsUserId, setPayoutsUserId] = useState<string | null>(null);
  const [payoutsData, setPayoutsData] = useState<PayoutEvent[]>([]);
  const [payoutsLoading, setPayoutsLoading] = useState(false);

  // Notification badges
  const [disputeCount, setDisputeCount] = useState(0);
  const [payoutCount, setPayoutCount] = useState(0);
  const [lastSeenPayoutCount, setLastSeenPayoutCount] = useState<number | null>(null);
  const [talentCount, setTalentCount] = useState(0);
  const [lastSeenTalentCount, setLastSeenTalentCount] = useState<number | null>(null);

  // Freeze modal
  const [freezeUserId, setFreezeUserId] = useState<string | null>(null);
  const [freezeType, setFreezeType] = useState<"temporary" | "permanent">("temporary");
  const [freezeReason, setFreezeReason] = useState("");
  const [freezeUntil, setFreezeUntil] = useState("");

  const fetchAll = useCallback(() => {
    adminApi.get("/users").then((r) => {
      setUsers(r.data);
      const total = (r.data || []).length;
      const stored = localStorage.getItem("lastSeenTalentCount");
      const lastSeen = stored !== null ? parseInt(stored) : total;
      if (lastSeenTalentCount === null) setLastSeenTalentCount(lastSeen);
      setTalentCount(Math.max(0, total - lastSeen));
    });
    adminApi.get("/orders").then((r) => setOrders(r.data));
    adminApi.get("/admins").then((r) => setAdmins(r.data));
    adminApi.get("/payouts").then((r) => {
      const total = (r.data || []).length;
      const stored = localStorage.getItem("lastSeenPayoutCount");
      const lastSeen = stored !== null ? parseInt(stored) : total;
      if (lastSeenPayoutCount === null) setLastSeenPayoutCount(lastSeen);
      const newCount = Math.max(0, total - lastSeen);
      setPayoutCount(newCount);
    }).catch(() => {});
    adminApi.get("/disputes").then((r) => {
      const open = (r.data || []).filter((d: { status: string }) =>
        d.status === "needs_response" || d.status === "warning_needs_response"
      );
      setDisputeCount(open.length);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const validateAdminPassword = (pw: string) =>
    pw.length >= 16 && /[A-Z]/.test(pw) && /[0-9]/.test(pw) && /[^A-Za-z0-9]/.test(pw);

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    const { name, email, password } = createAdmin;
    if (!validateAdminPassword(password)) {
      return setCreateError("Password must be ≥16 chars with uppercase, digit, and special char.");
    }
    try {
      await adminApi.post("/create-admin", { name, email, password });
      setCreateAdmin({ name: "", email: "", password: "" });
      setCreateError("");
      fetchAll();
    } catch (err: any) {
      setCreateError(err.response?.data?.msg || "Error creating admin");
    }
  };

  const handlePasswordChange = async () => {
    const path = passwordIsAdmin ? `/admins/${passwordId}/password` : `/users/${passwordId}/password`;
    await adminApi.patch(path, { password: newPassword });
    setPasswordId(null);
    setNewPassword("");
  };

  const confirmDelete = async () => {
    const path = confirmIsAdmin ? `/admins/${confirmId}` : `/users/${confirmId}`;
    await adminApi.delete(path);
    if (confirmIsAdmin) setAdmins((prev) => prev.filter((x) => x._id !== confirmId));
    else setUsers((prev) => prev.filter((x) => x._id !== confirmId));
    setConfirmId(null);
  };

  const openPayouts = async (userId: string) => {
    setPayoutsUserId(userId);
    setPayoutsLoading(true);
    try {
      const { data } = await adminApi.get(`/dj/${userId}/payouts`);
      setPayoutsData(data);
    } catch {
      setPayoutsData([]);
    } finally {
      setPayoutsLoading(false);
    }
  };

  const handleFreeze = async () => {
    if (!freezeUserId) return;
    try {
      const { data } = await adminApi.patch(`/users/${freezeUserId}/freeze`, {
        freezeType,
        freezeReason: freezeReason || undefined,
        frozenUntil: freezeType === "temporary" && freezeUntil ? freezeUntil : undefined,
      });
      setUsers((prev) => prev.map((u) => (u._id === freezeUserId ? data.user : u)));
      setFreezeUserId(null);
      setFreezeReason("");
      setFreezeUntil("");
    } catch (err: any) {
      alert(err.response?.data?.msg || "Error freezing account");
    }
  };

  const handleUnfreeze = async (userId: string) => {
    try {
      const { data } = await adminApi.patch(`/users/${userId}/unfreeze`);
      setUsers((prev) => prev.map((u) => (u._id === userId ? data.user : u)));
    } catch (err: any) {
      alert(err.response?.data?.msg || "Error");
    }
  };

  // Revenue tab aggregations
  const revenueStats = useMemo(() => {
    const paid = orders.filter((o) => o.paid);
    const byCurrency: Record<string, { volume: number; fees: number; djShare: number; count: number }> = {};
    for (const o of paid) {
      const cur = (o.currency || "usd").toUpperCase();
      if (!byCurrency[cur]) byCurrency[cur] = { volume: 0, fees: 0, djShare: 0, count: 0 };
      const total = typeof o.totalAmount === "number" && o.totalAmount > 0 ? o.totalAmount : o.amount / 0.85;
      const fee =
        typeof o.feeAmount === "number" && o.feeAmount > 0
          ? o.feeAmount
          : typeof o.totalAmount === "number" && o.totalAmount > o.amount
          ? o.totalAmount - o.amount
          : (o.amount * 0.15) / 0.85;
      byCurrency[cur].volume += total;
      byCurrency[cur].fees += fee;
      byCurrency[cur].djShare += o.amount;
      byCurrency[cur].count++;
    }

    // Top 10 DJs by all-time revenue
    const djTotals: Record<string, { name: string; avatar?: string; amount: number; count: number }> = {};
    for (const o of paid) {
      const id = typeof o.dj === "string" ? o.dj : o.dj._id;
      const name = typeof o.dj === "object" ? o.dj.name : users.find((u) => u._id === o.dj)?.name ?? "Unknown";
      const avatar = typeof o.dj === "object" ? o.dj.avatarUrl : users.find((u) => u._id === o.dj)?.avatarUrl;
      if (!djTotals[id]) djTotals[id] = { name, avatar, amount: 0, count: 0 };
      djTotals[id].amount += o.amount;
      djTotals[id].count++;
    }
    const topDjs = Object.entries(djTotals)
      .sort(([, a], [, b]) => b.amount - a.amount)
      .slice(0, 10)
      .map(([id, v]) => ({ id, ...v }));

    return { byCurrency, topDjs };
  }, [orders, users]);


  // Users search
  const q = userQuery.trim().toLowerCase();
  const filteredUsers = useMemo(() => {
    if (!q) return users;
    return users.filter((u) => [u.name, u.email, u.slug].some((v) => (v || "").toLowerCase().includes(q)));
  }, [users, q]);

  const payoutsUser = users.find((u) => u._id === payoutsUserId);

  const tabTitle = TABS.find((t) => t.id === tab)?.label ?? "Dashboard";

  return (
    <div id="adm-root-light">
      {/* ── Sidebar ── */}
      <aside id="adm-sidebar-light">
        <div className="adm-logo-light">
          <span style={{ color: "var(--adm-blue)", marginRight: 4 }}>R</span>Admin
        </div>
        <nav className="adm-menu">
          {TABS.map((t) => (
            <button
              key={t.id}
              className={`adm-menu-btn ${tab === t.id ? "active" : ""}`}
              onClick={() => {
                setTab(t.id);
                if (t.id === "disputes") setDisputeCount(0);
                if (t.id === "allPayouts") {
                  setPayoutCount(0);
                  adminApi.get("/payouts").then((r) => {
                    const total = (r.data || []).length;
                    localStorage.setItem("lastSeenPayoutCount", String(total));
                    setLastSeenPayoutCount(total);
                  }).catch(() => {});
                }
                if (t.id === "users") {
                  setTalentCount(0);
                  localStorage.setItem("lastSeenTalentCount", String(users.length));
                  setLastSeenTalentCount(users.length);
                }
              }}
            >
              <span className="menu-icon" style={t.id === "orders" ? { color: "var(--adm-blue)", fontWeight: 800, fontFamily: "sans-serif" } : undefined}>{t.icon}</span>
              {t.label}
              {t.id === "disputes" && disputeCount > 0 && (
                <span className="dispute-badge-dot">{disputeCount}</span>
              )}
              {t.id === "allPayouts" && payoutCount > 0 && (
                <span className="dispute-badge-dot">{payoutCount}</span>
              )}
              {t.id === "users" && talentCount > 0 && (
                <span className="dispute-badge-dot">{talentCount}</span>
              )}
            </button>
          ))}
        </nav>
        <div className="adm-spacer" />
        <div className="adm-sidebar-footer">
          <div className="adm-admin-info">
            <div className="adm-admin-dot" />
            <span className="adm-admin-name">{admin?.name}</span>
          </div>
          <button onClick={handleLogout} className="adm-logout-btn-light">
            Sign out
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <main id="adm-main-light">
        <header id="adm-header">
          <div>
            <h1 className="adm-heading">{tabTitle}</h1>
          </div>
          {tab === "users" && (
            <div className="adm-topbar">
              <input
                className="adm-search"
                placeholder="Search Talent..."
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
              />
              <button className="adm-btn-light" onClick={fetchAll}>
                ↻ Refresh
              </button>
            </div>
          )}
          {tab === "orders" && (
            <button className="adm-btn-light" onClick={fetchAll}>
              ↻ Refresh
            </button>
          )}
        </header>

        <div className="adm-content-area">
          {/* ── Overview ── */}
          {tab === "overview" && (
            <AdminOverviewTab users={users} orders={orders} adminsCount={admins.length} setTab={setTab} />
          )}

          {/* ── Orders ── */}
          {tab === "orders" && <AdminOrdersTab orders={orders} users={users} />}

          {/* ── Users/DJs ── */}
          {tab === "users" && (
            <section className="fade-in">
              <div className="adm-table-wrapper">
                <table className="adm-table-light">
                  <thead>
                    <tr>
                      <th>Talent</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th style={{ width: 300 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => {
                      const avatarSrc = fmtAvatar(u.avatarUrl);
                      return (
                        <tr key={u._id} className={u.isFrozen ? "row-frozen" : ""}>
                          <td>
                            <div
                              className="dj-cell clickable"
                              onClick={() => navigate(`/admin/dj/${u._id}`)}
                            >
                              {avatarSrc ? (
                                <img src={avatarSrc} className="dj-cell-avatar" alt="" />
                              ) : (
                                <div className="dj-cell-placeholder">{u.name[0].toUpperCase()}</div>
                              )}
                              <div>
                                <span className="dj-link-text" style={{ fontSize: "0.88rem", fontWeight: 600 }}>
                                  {u.name}
                                </span>
                                {u.slug && (
                                  <div className="muted-text" style={{ fontSize: "0.72rem" }}>
                                    @{u.slug}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="muted-text" style={{ fontSize: "0.85rem" }}>
                            {u.email}
                          </td>
                          <td>
                            {u.isFrozen ? (
                              <div>
                                <span className="badge badge-sm badge-frozen">
                                  {u.freezeType === "permanent" ? "Frozen" : "Temp. Frozen"}
                                </span>
                                {u.frozenUntil && (
                                  <div className="muted-text" style={{ fontSize: "0.7rem", marginTop: 2 }}>
                                    Until {new Date(u.frozenUntil).toLocaleDateString()}
                                  </div>
                                )}
                                {u.freezeReason && (
                                  <div className="muted-text" style={{ fontSize: "0.7rem" }}>
                                    {u.freezeReason}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="badge badge-sm badge-accepted">Active</span>
                            )}
                            <span className={`badge badge-sm ${u.isStripeOnboarded ? "badge-accepted" : "badge-frozen"}`} style={{ marginLeft: 8 }}>
                              Stripe
                            </span>
                            <span className={`badge badge-sm ${u.isSmsEnabled && u.phoneNumber ? "badge-accepted" : "badge-frozen"}`} style={{ marginLeft: 8 }}>
                              SMS
                            </span>
                          </td>
                          <td>
                            <div className="action-btn-group">
                              <button
                                className="adm-btn-sm adm-btn-outline"
                                onClick={() => openPayouts(u._id)}
                              >
                                Payouts
                              </button>
                              {u.isFrozen ? (
                                <button
                                  className="adm-btn-sm adm-btn-green-sm"
                                  onClick={() => handleUnfreeze(u._id)}
                                >
                                  Unfreeze
                                </button>
                              ) : (
                                <button
                                  className="adm-btn-sm adm-btn-orange-sm"
                                  onClick={() => {
                                    setFreezeUserId(u._id);
                                    setFreezeType("temporary");
                                    setFreezeReason("");
                                    setFreezeUntil("");
                                  }}
                                >
                                  Freeze
                                </button>
                              )}
                              <button
                                className="adm-btn-sm adm-btn-blue-sm"
                                onClick={() => {
                                  setPasswordId(u._id);
                                  setPasswordIsAdmin(false);
                                }}
                              >
                                Password
                              </button>
                              <button
                                className="adm-btn-sm adm-btn-red-sm"
                                onClick={() => {
                                  setConfirmId(u._id);
                                  setConfirmIsAdmin(false);
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredUsers.length === 0 && (
                      <tr>
                        <td colSpan={4} style={{ textAlign: "center", padding: 30, color: "#9ca3af" }}>
                          No Talent found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* ── Revenue ── */}
          {tab === "revenue" && (
            <section className="fade-in">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
                {/* Currency breakdown */}
                <div className="adm-card">
                  <h2 className="adm-section-title" style={{ marginBottom: 16 }}>
                    Revenue by Currency
                  </h2>
                  <table className="adm-table-light">
                    <thead>
                      <tr>
                        <th>Currency</th>
                        <th>Total Volume</th>
                        <th>Platform Fees</th>
                        <th>Talent Share</th>
                        <th>Orders</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(revenueStats.byCurrency).map(([cur, s]) => (
                        <tr key={cur} className="hover-row">
                          <td style={{ fontWeight: 700 }}>{cur}</td>
                          <td>${s.volume.toFixed(2)}</td>
                          <td style={{ color: "var(--adm-green)", fontWeight: 600 }}>${s.fees.toFixed(2)}</td>
                          <td>${s.djShare.toFixed(2)}</td>
                          <td>{s.count}</td>
                        </tr>
                      ))}
                      {Object.keys(revenueStats.byCurrency).length === 0 && (
                        <tr>
                          <td colSpan={5} style={{ textAlign: "center", padding: 20, color: "#9ca3af" }}>
                            No paid orders yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Quick revenue KPIs */}
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {[
                    {
                      label: "Total Platform Revenue (all time)",
                      value:
                        "$" +
                        Object.values(revenueStats.byCurrency)
                          .reduce((a, b) => a + b.fees, 0)
                          .toFixed(2),
                      color: "var(--adm-green)",
                    },
                    {
                      label: "Total Volume (all time)",
                      value:
                        "$" +
                        Object.values(revenueStats.byCurrency)
                          .reduce((a, b) => a + b.volume, 0)
                          .toFixed(2),
                      color: "",
                    },
                    {
                      label: "Total Talent Payouts",
                      value:
                        "$" +
                        Object.values(revenueStats.byCurrency)
                          .reduce((a, b) => a + b.djShare, 0)
                          .toFixed(2),
                      color: "var(--adm-blue)",
                    },
                    {
                      label: "Total Paid Orders",
                      value: String(Object.values(revenueStats.byCurrency).reduce((a, b) => a + b.count, 0)),
                      color: "",
                    },
                  ].map((k) => (
                    <div key={k.label} className="kpi-card" style={{ cursor: "default" }}>
                      <div>
                        <div className="kpi-label">{k.label}</div>
                        <div className="kpi-value" style={k.color ? { color: k.color } : {}}>
                          {k.value}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top DJs all-time */}
              <div className="adm-card">
                <h2 className="adm-section-title" style={{ marginBottom: 14 }}>
                  Top Talent — All Time Revenue
                </h2>
                <div className="adm-table-wrapper">
                  <table className="adm-table-light adm-table-compact">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Talent</th>
                        <th>Revenue (Talent Share)</th>
                        <th>Platform Fee</th>
                        <th>Orders</th>
                        <th>Avg. Value</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {revenueStats.topDjs.map((dj, i) => {
                        const fee = (dj.amount * 0.15) / 0.85;
                        const avg = dj.count > 0 ? dj.amount / dj.count : 0;
                        const src = fmtAvatar(dj.avatar);
                        return (
                          <tr
                            key={dj.id}
                            className="hover-row clickable"
                            onClick={() => navigate(`/admin/dj/${dj.id}`)}
                          >
                            <td className="muted-text" style={{ fontWeight: 700 }}>
                              {i + 1}
                            </td>
                            <td>
                              <div className="dj-cell dj-cell-compact">
                                {src ? (
                                  <img src={src} className="dj-cell-avatar-sm" alt="" />
                                ) : (
                                  <div className="dj-cell-placeholder-sm">{dj.name[0].toUpperCase()}</div>
                                )}
                                <span className="dj-link-text" style={{ fontSize: "0.82rem" }}>
                                  {dj.name}
                                </span>
                              </div>
                            </td>
                            <td style={{ fontWeight: 700 }}>${dj.amount.toFixed(2)}</td>
                            <td style={{ color: "var(--adm-green)" }}>${fee.toFixed(2)}</td>
                            <td>{dj.count}</td>
                            <td>${avg.toFixed(2)}</td>
                            <td>
                              <span style={{ color: "var(--adm-blue)", fontSize: "0.8rem" }}>View →</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          )}



          {/* ── All Payouts ── */}
          {tab === "allPayouts" && <AdminAllPayoutsTab />}

          {/* ── Trending ── */}
          {tab === "trending" && <AdminTrendingTab orders={orders} />}

          {/* ── Disputes ── */}
          {tab === "disputes" && <AdminDisputesTab />}

          {/* ── Establishments ── */}
          {tab === "establishments" && <AdminEstablishmentsTab allDjs={users} />}

          {/* ── Admins ── */}
          {tab === "admins" && (
            <section className="fade-in">
              <div className="adm-card" style={{ marginBottom: 20 }}>
                <h2 className="adm-section-title" style={{ marginBottom: 14 }}>
                  Create Admin
                </h2>
                <form onSubmit={handleCreateAdmin} className="adm-create-form">
                  <input
                    required
                    placeholder="Name"
                    value={createAdmin.name}
                    onChange={(e) => setCreateAdmin({ ...createAdmin, name: e.target.value })}
                  />
                  <input
                    required
                    type="email"
                    placeholder="Email"
                    value={createAdmin.email}
                    onChange={(e) => setCreateAdmin({ ...createAdmin, email: e.target.value })}
                  />
                  <input
                    required
                    type="password"
                    placeholder="Password (≥16 chars)"
                    value={createAdmin.password}
                    onChange={(e) => setCreateAdmin({ ...createAdmin, password: e.target.value })}
                  />
                  <button className="adm-btn-green" type="submit">
                    Create
                  </button>
                </form>
                {createError && <p className="error-text">{createError}</p>}
              </div>

              <div className="adm-table-wrapper">
                <table className="adm-table-light">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {admins.map((a) => (
                      <tr key={a._id}>
                        <td style={{ fontWeight: 600 }}>{a.name}</td>
                        <td className="muted-text">{a.email}</td>
                        <td>
                          <div className="action-btn-group">
                            <button
                              className="adm-btn-sm adm-btn-blue-sm"
                              onClick={() => {
                                setPasswordId(a._id);
                                setPasswordIsAdmin(true);
                              }}
                            >
                              Password
                            </button>
                            <button
                              className="adm-btn-sm adm-btn-red-sm"
                              onClick={() => {
                                setConfirmId(a._id);
                                setConfirmIsAdmin(true);
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>

        {/* ── Delete modal ── */}
        {confirmId && (
          <div className="adm-modal-backdrop fade-in">
            <div className="adm-modal-box">
              <h3 style={{ marginBottom: 8 }}>Confirm Delete</h3>
              <p className="muted-text" style={{ marginBottom: 16, fontSize: "0.88rem" }}>
                This action cannot be undone.
              </p>
              <div className="adm-modal-actions">
                <button className="adm-btn-ghost" onClick={() => setConfirmId(null)}>
                  Cancel
                </button>
                <button className="adm-btn-red" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Password modal ── */}
        {passwordId && (
          <div className="adm-modal-backdrop fade-in">
            <div className="adm-modal-box">
              <h3 style={{ marginBottom: 12 }}>Change Password</h3>
              <input
                type="password"
                placeholder="New password..."
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={{
                  width: "100%",
                  marginBottom: 12,
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: "1px solid var(--adm-line)",
                  fontSize: "0.88rem",
                }}
              />
              <div className="adm-modal-actions">
                <button
                  className="adm-btn-ghost"
                  onClick={() => {
                    setPasswordId(null);
                    setNewPassword("");
                  }}
                >
                  Cancel
                </button>
                <button className="adm-btn-green" onClick={handlePasswordChange}>
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Freeze modal ── */}
        {freezeUserId && (
          <div className="adm-modal-backdrop fade-in">
            <div className="adm-modal-box">
              <h3 style={{ marginBottom: 4 }}>Freeze Account</h3>
              <p className="muted-text" style={{ marginBottom: 16, fontSize: "0.85rem" }}>
                {users.find((u) => u._id === freezeUserId)?.name}
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div>
                  <label className="kpi-label" style={{ display: "block", marginBottom: 4 }}>
                    Freeze Type
                  </label>
                  <select
                    className="adm-select"
                    style={{ width: "100%" }}
                    value={freezeType}
                    onChange={(e) => setFreezeType(e.target.value as "temporary" | "permanent")}
                  >
                    <option value="temporary">Temporary</option>
                    <option value="permanent">Permanent</option>
                  </select>
                </div>
                {freezeType === "temporary" && (
                  <div>
                    <label className="kpi-label" style={{ display: "block", marginBottom: 4 }}>
                      Freeze Until
                    </label>
                    <input
                      type="date"
                      className="adm-search"
                      style={{ width: "100%" }}
                      value={freezeUntil}
                      onChange={(e) => setFreezeUntil(e.target.value)}
                    />
                  </div>
                )}
                <div>
                  <label className="kpi-label" style={{ display: "block", marginBottom: 4 }}>
                    Reason (optional)
                  </label>
                  <input
                    className="adm-search"
                    style={{ width: "100%" }}
                    placeholder="e.g. Violation of terms..."
                    value={freezeReason}
                    onChange={(e) => setFreezeReason(e.target.value)}
                  />
                </div>
              </div>
              <div className="adm-modal-actions">
                <button className="adm-btn-ghost" onClick={() => setFreezeUserId(null)}>
                  Cancel
                </button>
                <button className="adm-btn-red" onClick={handleFreeze}>
                  Freeze Account
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Payouts modal ── */}
        {payoutsUserId && (
          <div className="adm-modal-backdrop fade-in" onClick={() => setPayoutsUserId(null)}>
            <div className="adm-modal-box adm-modal-wide" onClick={(e) => e.stopPropagation()}>
              <div className="card-header-flex" style={{ marginBottom: 14 }}>
                <h3 style={{ margin: 0 }}>Payout History — {payoutsUser?.name}</h3>
                <button className="adm-btn-ghost-sm" onClick={() => setPayoutsUserId(null)}>
                  ✕ Close
                </button>
              </div>
              {payoutsLoading ? (
                <p className="muted-text" style={{ textAlign: "center", padding: 20 }}>
                  Loading...
                </p>
              ) : payoutsData.length === 0 ? (
                <p className="muted-text" style={{ textAlign: "center", padding: 20 }}>
                  No payout history found.
                </p>
              ) : (
                <div className="adm-table-wrapper" style={{ maxHeight: 380, overflowY: "auto" }}>
                  <table className="adm-table-light adm-table-compact sticky-header">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Time</th>
                        <th>Gross</th>
                        <th>Net (Talent)</th>
                        <th>Orders</th>
                        <th>Accepted</th>
                        <th>Declined</th>
                        <th>Avg. Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payoutsData.map((p) => (
                        <tr key={p._id} className="hover-row">
                          <td>{new Date(p.createdAt).toLocaleDateString()}</td>
                          <td className="muted-text">
                            {new Date(p.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </td>
                          <td style={{ fontWeight: 600 }}>${p.totalGross.toFixed(2)}</td>
                          <td style={{ fontWeight: 700, color: "var(--adm-green)" }}>
                            ${p.totalNet.toFixed(2)}
                          </td>
                          <td>{p.ordersCount}</td>
                          <td style={{ color: "var(--adm-green)" }}>{p.ordersAccepted}</td>
                          <td style={{ color: "var(--adm-red)" }}>{p.ordersDeclined}</td>
                          <td>${p.avgRequestValue.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
